import courseJson from "../../WordPhysics/Resources/course.json";
import layoutsJson from "../../WordPhysics/Resources/layouts.json";
import seedJson from "../../WordPhysics/Resources/seed_scores.json";
import freePoolJson from "../../WordPhysics/Resources/free_pool.json";
import jaRaw from "../../WordPhysics/Resources/ja.lproj/Localizable.strings?raw";
import type { CourseRegistry, Evaluation, FreePoolRegistry, Layout, LayoutRegistry, Lesson, ProjectileProfile, SeedRegistry } from "./types";
import { tuning } from "./game/tuning";

export const course = courseJson as CourseRegistry;
export const layouts = layoutsJson as LayoutRegistry;
const seed = seedJson as SeedRegistry;
const freePool = freePoolJson as FreePoolRegistry;

const translations = new Map<string, string>();
for (const line of jaRaw.split("\n")) {
  const match = line.match(/^\s*"((?:\\.|[^"])*)"\s*=\s*"((?:\\.|[^"])*)"\s*;/);
  if (match) translations.set(match[1].replaceAll('\\"', '"'), match[2].replaceAll('\\"', '"'));
}

export function ja(value: string): string {
  return translations.get(value) ?? value;
}

export function evaluateSeed(word: string, clueSetId: string): Evaluation | null {
  const normalized = word.trim().toLowerCase().replace(/\s+/g, " ");
  const score = seed.scores[clueSetId]?.[normalized];
  return score ? { ...score, word: normalized, source: "seed" } : null;
}

export function profileFor(tiers: number[]): ProjectileProfile {
  const clamped = tiers.map((value) => Math.max(0, Math.min(4, value)));
  return {
    mass: tuning.massTiers[clamped[0] ?? 0],
    speedPosition: tuning.speedTiers[clamped[1] ?? 0],
    blastRadius: tuning.blastTiers[clamped[2] ?? 0],
    canDetonate: (clamped[2] ?? 0) >= tuning.detonateMinTier,
    bounce: 0.25,
  };
}

export const allLessons = course.units.flatMap((unit) => unit.lessons);
export const layoutById = new Map(layouts.layouts.map((layout) => [layout.id, layout]));

export function makeFreeChallenge(): { lesson: Lesson; layout: Layout } {
  const entry = freePool.entries[Math.floor(Math.random() * freePool.entries.length)];
  const candidates = allLessons.filter((lesson) => entry.compatible_clue_set_ids.includes(lesson.clue_set.id));
  const source = candidates[Math.floor(Math.random() * candidates.length)] ?? allLessons[0];
  return {
    lesson: { ...source, id: `free-${crypto.randomUUID()}`, code: "FREE", layout_id: entry.layout_id, shots: 3 },
    layout: layoutById.get(entry.layout_id)!,
  };
}

export function localDateKey(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** The same board and clues for every player on a given calendar day. */
export function makeDailyChallenge(dateKey = localDateKey()): { lesson: Lesson; layout: Layout } {
  const hash = hashString(`daily:${dateKey}`);
  const entry = freePool.entries[hash % freePool.entries.length];
  const candidates = allLessons.filter((lesson) => entry.compatible_clue_set_ids.includes(lesson.clue_set.id));
  const source = candidates[Math.floor(hash / freePool.entries.length) % Math.max(1, candidates.length)] ?? allLessons[0];
  return {
    lesson: { ...source, id: `daily-${dateKey}`, code: "DAILY", layout_id: entry.layout_id, shots: 3 },
    layout: layoutById.get(entry.layout_id)!,
  };
}

export const appIconUrl = new URL(
  "../../WordPhysics/Resources/Assets.xcassets/AppIcon.appiconset/AppIcon-1024.png",
  import.meta.url,
).href;

export const impactSoundUrl = new URL("../../WordPhysics/Resources/glass-impact.wav", import.meta.url).href;
